import React from 'react'
import { cn } from '@/lib/utils/cn'
import { type TextSize, textSizeClasses } from '@/lib/utils/text-size-utils'
import { type LucideIcon } from 'lucide-react'

/**
 * Display modes for the description component
 */
type DisplayMode = 'single-line' | 'multi-line' | 'full' | 'truncated'

/**
 * Props for the NodePropertyString component
 */
interface NodePropertyStringProps {
  /**
   * The description text to display
   */
  description?: string | null
  /**
   * Optional label to display before the description (defaults to empty string)
   */
  label?: string
  /**
   * Optional icon to display before the text
   */
  icon?: LucideIcon
  /**
   * Text size variant (defaults to 'xs')
   */
  textSize?: TextSize
  /**
   * Display mode for the description text (defaults to 'single-line')
   */
  displayMode?: DisplayMode
  /**
   * Maximum number of lines to show when using line-clamp (defaults to 1 for single-line, 3 for multi-line)
   */
  maxLines?: number
  /**
   * Maximum character length before truncating (only applies when displayMode is 'truncated')
   */
  maxLength?: number
  /**
   * Additional CSS classes for the container
   */
  className?: string
  /**
   * Whether to use hover mode (truncated text, full text reveals on hover)
   */
  hoverMode?: boolean
  /**
   * Whether to show the description only when it's not empty (defaults to true)
   */
  showOnlyIfNotEmpty?: boolean
  /**
   * Custom truncation suffix (defaults to '...')
   */
  truncationSuffix?: string
}

/**
 * Truncate text to a specific length with a suffix
 */
function truncateText(text: string, maxLength: number, suffix: string = '...'): string {
  if (text.length <= maxLength) {
    return text
  }
  return text.slice(0, maxLength - suffix.length) + suffix
}

/**
 * Get line clamp class based on max lines
 */
function getLineClampClass(maxLines: number): string {
  const clampMap: Record<number, string> = {
    1: 'line-clamp-1',
    2: 'line-clamp-2',
    3: 'line-clamp-3',
    4: 'line-clamp-4',
    5: 'line-clamp-5',
    6: 'line-clamp-6',
  }
  return clampMap[maxLines] || 'line-clamp-3'
}

/**
 * A reusable component for displaying node descriptions with consistent styling
 * Supports various display modes including truncation, line clamping, and hover modes
 *
 * @example
 * ```tsx
 * // Basic usage - single line with truncation
 * <NodePropertyString description="This is a long description that will be truncated" />
 *
 * // Multi-line with custom line limit
 * <NodePropertyString
 *   description="This is a longer description that spans multiple lines and will be clamped to 3 lines"
 *   displayMode="multi-line"
 *   maxLines={3}
 * />
 *
 * // Full text display (no truncation)
 * <NodePropertyString
 *   description="This is the complete description text with no truncation applied"
 *   displayMode="full"
 * />
 *
 * // Truncated to specific character length
 * <NodePropertyString
 *   description="This is a very long description that needs to be truncated to a specific length"
 *   displayMode="truncated"
 *   maxLength={50}
 * />
 *
 * // With custom label
 * <NodePropertyString
 *   description="Project description text"
 *   label="Description:"
 * />
 *
 * // With custom styling and larger text
 * <NodePropertyString
 *   description="Description text"
 *   textSize="sm"
 *   className="font-medium"
 * />
 *
 * // Hover mode (truncated, full text on hover)
 * <NodePropertyString
 *   description="This is a long description that will be truncated but show full text on hover"
 *   hoverMode
 * />
 *
 * // Custom truncation suffix
 * <NodePropertyString
 *   description="Long description text"
 *   displayMode="truncated"
 *   maxLength={20}
 *   truncationSuffix="[...]"
 * />
 *
 * // With icon
 * <NodePropertyString
 *   description="user@example.com"
 *   icon={Mail}
 *   textSize="sm"
 * />
 * ```
 */
export function NodePropertyString({
  description,
  label = '',
  icon,
  textSize = 'xs',
  displayMode = 'single-line',
  maxLines,
  maxLength,
  className = '',
  hoverMode = false,
  showOnlyIfNotEmpty = true,
  truncationSuffix = '...',
}: NodePropertyStringProps) {
  // Don't render if description is empty/null and showOnlyIfNotEmpty is true
  if (showOnlyIfNotEmpty && (!description || description.trim() === '')) {
    return null
  }

  // Determine the appropriate max lines based on display mode
  const defaultMaxLines = displayMode === 'single-line' ? 1 : 3
  const effectiveMaxLines = maxLines || defaultMaxLines

  // Process the description text based on display mode
  let processedDescription = description || ''

  if (displayMode === 'truncated' && maxLength) {
    processedDescription = truncateText(processedDescription, maxLength, truncationSuffix)
  }

  const content = (
    <div className={cn('flex flex-col gap-1', className)}>
      {label && (
        <span className={cn(textSizeClasses[textSize], 'text-muted-foreground font-medium')}>
          {label}
        </span>
      )}
      <div className="flex items-center gap-2">
        {icon &&
          React.createElement(icon, { className: 'h-4 w-4 flex-shrink-0 text-muted-foreground' })}
        <p
          className={cn(
            textSizeClasses[textSize],
            displayMode !== 'full' && getLineClampClass(effectiveMaxLines)
          )}
          title={hoverMode && displayMode !== 'full' ? description || undefined : undefined}
        >
          {processedDescription}
        </p>
      </div>
    </div>
  )

  if (hoverMode && displayMode !== 'full') {
    return (
      <div className="group relative">
        {content}
        {description && processedDescription !== description && (
          <div className="absolute left-0 top-full mt-1 opacity-0 group-hover:opacity-100 transition-opacity bg-popover text-popover-foreground text-xs px-2 py-1 rounded shadow-md z-10 max-w-xs break-words">
            {description}
          </div>
        )}
      </div>
    )
  }

  return content
}
